// core/registry.ts
import type { WidgetKey, ComponentModule } from '@/types/core';
import { EiWidget } from '@/core/EiWidget';

type ModuleFactory = () => Promise<ComponentModule<any>>;

const factories = new Map<WidgetKey, ModuleFactory>();
const moduleCache = new Map<WidgetKey, Promise<ComponentModule<any>>>();
const instanceIndex = new WeakMap<HTMLElement, EiWidget<any>>();

const _elements = new Set<HTMLElement>();

export function registerWidget(key: WidgetKey, factory: ModuleFactory) {
    factories.set(key, factory);
}

export function allInstances(): Array<{ el: HTMLElement; widget: EiWidget<any> }> {
    // WeakMap can't be iterated; if you want this, maintain a parallel Set.
    // Minimal approach: add a Set tracking elements as keys:
    return Array.from(_elements).map((el) => ({ el, widget: instanceIndex.get(el)! }));
}

export function hasInstance(el: HTMLElement) {
    return instanceIndex.has(el);
}
export function getInstance<P>(el: HTMLElement) {
    return instanceIndex.get(el) as EiWidget<P> | undefined;
}
export function setInstance<P>(el: HTMLElement, inst: EiWidget<P>) {
    instanceIndex.set(el, inst);
    _elements.add(el);
}
export function deleteInstance(el: HTMLElement) {
    instanceIndex.delete(el);
    _elements.delete(el);
}

export async function loadModule(key: WidgetKey): Promise<ComponentModule<any>> {
    if (!moduleCache.has(key)) {
        const f = factories.get(key);
        if (!f) throw new Error(`No widget factory for key "${key}"`);
        moduleCache.set(key, f());
    }
    return moduleCache.get(key)!;
}

export function evictModule(key: WidgetKey) {
    moduleCache.delete(key);
}
